<template>
  <!-- Mobile: a button that opens the bottom-sheet calendar -->
  <button
    v-if="isMobile"
    type="button"
    class="w-full flex items-center gap-2 border border-surface-300 rounded-lg px-3 py-2 text-sm text-start bg-white"
    :class="{ 'text-surface-400': !hasValue }"
    @click="openSheet"
  >
    <i class="pi pi-calendar text-surface-500 !text-xs flex-none" />
    <span class="truncate">{{ label }}</span>
  </button>

  <!-- Desktop: the regular PrimeVue date picker -->
  <DatePicker
    v-else
    :modelValue="modelValue as any"
    :selectionMode="mode === 'range' ? 'range' : 'single'"
    :minDate="minDate"
    :maxDate="maxDate"
    :dateFormat="dateFormat"
    :manualInput="false"
    :placeholder="placeholder"
    touchUI
    class="w-full"
    @update:modelValue="onDesktopUpdate"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DatePicker from 'primevue/datepicker';
import { useResponsive } from '@app/composables/responsive';
import { useDrawer } from '@torim-app/shared-components';
import SettingsDateSheet from '@app/components/admin/pages/configurations/ui/SettingsDateSheet.vue';

/**
 * A date field that renders the PrimeVue DatePicker on desktop and the mobile-friendly
 * SettingsDateSheet bottom-sheet on mobile. Supports single dates and ranges.
 * v-model is a Date (single) or a [start, end] Date[] (range), matching PrimeVue.
 */
const props = withDefaults(defineProps<{
  // Accepts string too because some existing forms type their date field loosely (string | Date).
  modelValue?: Date | Date[] | string | null;
  mode?: 'single' | 'range';
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  title?: string;      // drawer title on mobile
  dateFormat?: string; // desktop display format
}>(), {
  mode: 'single',
  dateFormat: 'dd-mm-yy',
  title: 'בחירת תאריך',
  placeholder: 'בחרו תאריך',
});

const emit = defineEmits<{ 'update:modelValue': [value: Date | Date[] | undefined] }>();

const { isMobile } = useResponsive();
const { openDrawer } = useDrawer();

const asDate = (v: unknown): Date | undefined => {
  if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
  if (typeof v === 'string' && v) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};
const rangeArr = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

const hasValue = computed(() =>
  props.mode === 'range' ? rangeArr.value.filter(Boolean).length > 0 : !!asDate(props.modelValue));

const fmtDate = (d?: Date) => (d ? `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}` : '');

const label = computed(() => {
  if (props.mode === 'range') {
    const [s, e] = rangeArr.value;
    if (!asDate(s)) return props.placeholder;
    return `${fmtDate(asDate(s))} - ${asDate(e) ? fmtDate(asDate(e)) : '…'}`;
  }
  const d = asDate(props.modelValue);
  return d ? fmtDate(d) : props.placeholder;
});

const onDesktopUpdate = (v: Date | Date[] | null) => emit('update:modelValue', v ?? undefined);

const openSheet = () => {
  if (props.mode === 'range') {
    const [s, e] = rangeArr.value;
    openDrawer(SettingsDateSheet, {
      mode: 'range',
      startDate: asDate(s),
      endDate: asDate(e),
      minDate: props.minDate,
      maxDate: props.maxDate,
      onPickRange: (start: Date, end: Date) => emit('update:modelValue', [start, end]),
    }, { title: props.title });
  } else {
    openDrawer(SettingsDateSheet, {
      date: asDate(props.modelValue),
      minDate: props.minDate,
      maxDate: props.maxDate,
      onPick: (picked: Date) => emit('update:modelValue', picked),
    }, { title: props.title });
  }
};
</script>
